<div class="thresholds-section">
  <!-- Guidance Banner (Empty State) -->
  @if (bands.length === 0 && !readonly) {
    <div class="threshold-guidance" role="note">
      <span class="material-icons" aria-hidden="true">info</span>
      <div class="threshold-guidance-body">
        <div class="threshold-guidance-title">{{ 'No thresholds defined yet' | translate }}</div>
        <p class="threshold-guidance-text">
          {{ 'Define threshold bands to color-code this parameter on dashboards' | translate }}
        </p>
      </div>
    </div>
  }

  <!-- Threshold Table -->
  <div class="threshold-table" role="table" [attr.aria-label]="'Threshold bands'">
    <!-- Table Header -->
    <div class="threshold-table-header" role="row">
      <span role="columnheader">{{ 'Label' | translate }}</span>
      <span role="columnheader">{{ 'Min' | translate }}</span>
      <span role="columnheader">{{ 'Max' | translate }}</span>
      <span role="columnheader">{{ 'Color' | translate }}</span>
      <span role="columnheader" aria-label="Delete band"></span>
    </div>

    <!-- Empty Row (Non-editable Placeholder) -->
    @if (bands.length === 0) {
      <div class="threshold-empty-row threshold-row-placeholder" role="row" [attr.aria-label]="'First threshold band placeholder'">
        <div role="cell">
          <input
            type="text"
            class="threshold-input"
            placeholder="{{ 'e.g. Normal' | translate }}"
            [attr.aria-label]="'Band label'"
            disabled
            aria-disabled="true"
            />
          </div>
          <div role="cell">
            <input
              type="number"
              class="threshold-input"
              placeholder="0"
              [attr.aria-label]="'Minimum value'"
              disabled
              aria-disabled="true"
              />
            </div>
            <div role="cell">
              <div class="max-field-wrap">
                <input
                  type="number"
                  class="threshold-input"
                  [placeholder]="'Max' | translate"
                  [attr.aria-label]="'Maximum value'"
                  disabled
                  aria-disabled="true"
                  />
                  <button
                    type="button"
                    class="infinity-btn"
                    [attr.aria-label]="'No upper bound'"
                    [attr.aria-pressed]="false"
                    disabled
                    aria-disabled="true"
                  >∞</button>
                </div>
              </div>
              <div role="cell">
                <div class="color-cell">
                  <button
                    type="button"
                    class="color-swatch-btn"
                    [attr.aria-label]="'Color swatch — no color selected yet'"
                    disabled
                    aria-disabled="true"
                  ></button>
                  <input
                    type="text"
                    class="hex-input"
                    placeholder="#hex"
                    [attr.aria-label]="'Hex color value'"
                    maxlength="7"
                    disabled
                    aria-disabled="true"
                    />
                  </div>
                </div>
                <div role="cell">
                  <button
                    type="button"
                    class="delete-row-btn"
                    [attr.aria-label]="'Delete this band'"
                    disabled
                    aria-disabled="true"
                    style="opacity: 0.3; cursor: not-allowed;"
                    >
                    <span class="material-icons" aria-hidden="true">delete_outline</span>
                  </button>
                </div>
              </div>
            }

            <!-- Threshold Rows -->
            @for (band of bands; track trackByBandIndex(idx, band); let idx = $index) {
              <div
                class="threshold-row"
                role="row"
                [attr.aria-label]="'Threshold band ' + (idx + 1)"
                >
                <!-- Label cell -->
                <div role="cell">
                  <input
                    type="text"
                    class="threshold-input"
                    [(ngModel)]="band.label"
                    placeholder="{{ 'Label' | translate }}"
                    [readonly]="readonly"
                    [ngClass]="{ error: fieldErrors['band_' + idx + '_label'] }"
                    [attr.aria-label]="'Band label'"
                    />
                  </div>
                  <!-- Min value cell -->
                  <div role="cell">
                    <input
                      type="number"
                      class="threshold-input"
                      [(ngModel)]="band.minValue"
                      (change)="onMinValueChange(idx)"
                      [readonly]="readonly"
                      [ngClass]="{ error: fieldErrors['band_' + idx + '_min'] }"
                      [attr.aria-label]="'Minimum value'"
                      />
                    </div>
                    <!-- Max value cell with infinity toggle -->
                    <div role="cell">
                      <div class="max-field-wrap">
                        <input
                          type="number"
                          class="threshold-input"
                          [(ngModel)]="band.maxValue"
                          (change)="onMaxValueChange(idx)"
                          [readonly]="readonly || band.noUpperBound"
                          [ngClass]="{ error: fieldErrors['band_' + idx + '_max'] }"
                          [attr.aria-label]="'Maximum value'"
                          />
                          <button
                            type="button"
                            class="infinity-btn"
                            [class.active]="band.noUpperBound"
                            (click)="toggleInfinity(idx)"
                            [disabled]="readonly || idx !== bands.length - 1"
                            [attr.aria-label]="band.noUpperBound ? 'No upper bound set' : 'Set no upper bound'"
                            [attr.aria-pressed]="band.noUpperBound"
                          >∞</button>
                        </div>
                      </div>
                      <!-- Color cell -->
                      <div role="cell">
                        <div class="color-cell">
                          <input
                            #colorPicker
                            type="color"
                            style="display:none"
                            [value]="band.colorHex"
                            (input)="band.colorHex = $any($event.target).value; onColorChange(idx)"
                            />
                            <button
                              type="button"
                              class="color-swatch-btn"
                              [style.background-color]="band.colorHex"
                              [attr.aria-label]="'Color swatch, hex ' + band.colorHex"
                              [disabled]="readonly"
                              (click)="!readonly && colorPicker.click()"
                            ></button>
                            <input
                              type="text"
                              class="hex-input"
                              [(ngModel)]="band.colorHex"
                              (change)="onColorChange(idx)"
                              [readonly]="readonly"
                              placeholder="#hex"
                              maxlength="7"
                              [attr.aria-label]="'Hex color value'"
                              />
                            </div>
                          </div>
                          <!-- Delete button cell -->
                          <div role="cell">
                            <button
                              type="button"
                              class="delete-row-btn"
                              (click)="deleteBand(idx)"
                              [disabled]="readonly"
                              [attr.aria-label]="'Delete this band'"
                              >
                              <span class="material-icons" aria-hidden="true">delete_outline</span>
                            </button>
                          </div>
                        </div>
                      }
                    </div><!-- /.threshold-table -->

                    <!-- Inline Validation Message -->
                    @if (hasValidationErrorsFlag) {
                      <div
                        class="threshold-validation visible"
                        role="alert"
                        >
                        @for (error of validationErrors; track trackByErrorIndex($index, error)) {
                          <div>{{ error }}</div>
                        }
                      </div>
                    }

                    <!-- Add Band Button -->
                    @if (!readonly) {
                      <button
                        type="button"
                        class="add-band-btn"
                        (click)="addBand()"
                        [disabled]="readonly || isSaving"
                        >
                        <span class="material-icons" aria-hidden="true">add</span>
                        {{ 'Add Band' | translate }}
                      </button>
                    }

                    <!-- Preview Bar Section -->
                    @if (bands.length > 0) {
                      <div class="preview-bar-wrap">
                        <div class="preview-bar-label" aria-hidden="true">{{ 'Preview' | translate }}</div>
                        <div class="preview-bar" role="img" [attr.aria-label]="'Threshold preview bar'">
                          @for (segment of previewSegments; track trackBySegmentIndex($index, segment)) {
                            <div
                              class="preview-band"
                              [style.background-color]="segment.colorHex"
                              [style.flex]="segment.flex"
                            ></div>
                          }
                        </div>
                        @if (bands.length > 0) {
                          <div class="preview-legend">
                            @for (item of previewLegendItems; track $index) {
                              <div class="preview-legend-item">
                                <div class="preview-legend-swatch" [style.background-color]="item.colorHex"></div>
                                <span>{{ item.label }}</span>
                              </div>
                            }
                          </div>
                        }
                      </div>
                    }
                  </div>
